import { session } from "../auth/session.js";
import { injectTheme } from "./theme.js";
import { mountGhostLogo } from "./ghostLogo.js";

const STYLE_ID = "ghostland-verify-style";
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-verify-overlay {
  position: fixed; inset: 0; z-index: 11;
  display: flex; align-items: center; justify-content: center;
  background: #000; color: #f4f4f4;
}
/* this screen is intentionally PIXEL-styled (overrides the global sans face) */
#gl-verify-overlay, #gl-verify-overlay * { font-family: 'Silkscreen', ui-monospace, monospace !important; }

/* chunky pixel frame — double inset rule + hard drop-shadow, white corner rivets */
#gl-verify-card {
  position: relative;
  width: 360px; padding: 40px 32px 32px; text-align: center;
  background: #0b0b0b; border: 3px solid #f4f4f4; border-radius: 0;
  box-shadow:
    inset 0 0 0 3px #000,
    inset 0 0 0 6px #2c2c2c,
    0 0 0 3px #000,
    10px 10px 0 0 rgba(0,0,0,.6);
}
.gl-verify-corner { position: absolute; width: 10px; height: 10px; background: #f4f4f4; box-shadow: 0 0 0 3px #000; }
.gl-verify-corner.tl { top: -7px; left: -7px; }
.gl-verify-corner.tr { top: -7px; right: -7px; }
.gl-verify-corner.bl { bottom: -7px; left: -7px; }
.gl-verify-corner.br { bottom: -7px; right: -7px; }

#gl-verify-card h1 {
  margin: 0 0 16px; font-size: 19px; letter-spacing: 0.06em; text-transform: uppercase;
  color: #f4f4f4; text-shadow: 0 2px 0 #000;
}
#gl-verify-card .sub { margin: 0 0 8px; font-size: 12px; color: #9a9a9a; line-height: 1.7; }
#gl-verify-card .em { color: #f4f4f4; }
#gl-verify-card button.primary {
  width: 100%; margin-top: 24px; padding: 14px 12px; cursor: pointer;
  background: #f4f4f4; color: #000; border: 3px solid #fff; border-radius: 0;
  font-size: 13px; letter-spacing: 0.14em; text-transform: uppercase;
  box-shadow: 4px 4px 0 0 #000;
}
#gl-verify-card button.primary:hover:not(:disabled) { background: #fff; }
#gl-verify-card button.primary:active:not(:disabled) { transform: translate(2px, 2px); box-shadow: 2px 2px 0 0 #000; }
#gl-verify-card button.primary:disabled { opacity: 0.4; cursor: default; }
#gl-verify-row { margin-top: 20px; display: flex; justify-content: center; gap: 22px; font-size: 11px; letter-spacing: 0.06em; }
#gl-verify-row a { color: #8a8a8a; cursor: pointer; text-decoration: underline; }
#gl-verify-row a:hover { color: #f4f4f4; }
#gl-verify-msg { margin-top: 16px; min-height: 14px; font-size: 11px; color: #9a9a9a; letter-spacing: 0.04em; }
#gl-verify-msg.err { color: var(--gl-error); }
`;

function ensureStyle(): void {
  injectTheme();
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
}

/**
 * The post-register gate: the account exists but its email isn't confirmed yet, so the player
 * can't reach character creation. Resolves "ok" once the email is confirmed (re-checked via
 * the refresh session), or "logout" to switch accounts.
 */
export function showVerifyEmail(): Promise<"ok" | "logout"> {
  ensureStyle();
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.id = "gl-verify-overlay";
    const who = session.email ? `<span class="em">${session.email}</span>` : "your inbox";
    overlay.innerHTML = `
      <div id="gl-verify-card">
        <span class="gl-verify-corner tl"></span>
        <span class="gl-verify-corner tr"></span>
        <span class="gl-verify-corner bl"></span>
        <span class="gl-verify-corner br"></span>
        <h1>Confirm your email</h1>
        <p class="sub">We sent a confirmation link to ${who}.</p>
        <p class="sub">Click it, then continue to create your character.</p>
        <button class="primary" id="gl-verify-continue" type="button">I've confirmed — continue</button>
        <div id="gl-verify-row">
          <a id="gl-verify-resend">Resend email</a>
          <a id="gl-verify-logout">Log out</a>
        </div>
        <div id="gl-verify-msg"></div>
      </div>`;
    document.body.appendChild(overlay);

    const logo = mountGhostLogo(overlay);

    const cont = overlay.querySelector<HTMLButtonElement>("#gl-verify-continue")!;
    const resend = overlay.querySelector<HTMLAnchorElement>("#gl-verify-resend")!;
    const logout = overlay.querySelector<HTMLAnchorElement>("#gl-verify-logout")!;
    const msg = overlay.querySelector<HTMLDivElement>("#gl-verify-msg")!;
    const setMsg = (t: string, err = false) => { msg.textContent = t; msg.classList.toggle("err", err); };

    cont.addEventListener("click", async () => {
      cont.disabled = true; setMsg("Checking…");
      await session.bootstrap(); // re-mint session → picks up the new emailVerified
      if (session.emailVerified !== false) { logo.dispose(); overlay.remove(); resolve("ok"); return; }
      cont.disabled = false; setMsg("Not confirmed yet — check your email (and spam).", true);
    });
    resend.addEventListener("click", async () => {
      setMsg("Sending…");
      try {
        await session.resendVerification();
        setMsg("Confirmation email sent — check your inbox (and spam).");
      } catch (err) {
        setMsg(err instanceof Error ? err.message : "Couldn't resend right now.", true);
      }
    });
    logout.addEventListener("click", () => { logo.dispose(); overlay.remove(); resolve("logout"); });
  });
}
